import React, { useEffect, useState } from 'react';
import { push as Push, replace as Replace } from 'redux-first-history';
import { connect, useDispatch } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { compose } from 'react-recompose';
import { FormList as FormListModule } from '@dev/ee-modules';
import { Form, FormTypeEnum } from '@dev/ee-api/ee-api-models';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import {
    ArrayOfFormsDeleteStart,
    CheckIfHaveClassicFormsStart,
    CopyFormStart,
    FormDeleteStart,
    LoadingFormsStart,
    StartLoadingFormsParams,
} from '@/store/forms/actions';
import Props from './FormListInterface';
import { FormsPaths } from '../../services/dictionaries/Paths';
import { HideDialog, ShowDialog, UpdateDialog } from '../../store/dialog/actions';
import { ShowDialogParams } from '../../store/dialog/types';
import { FormListProps } from './FormListInterface';
import { ApplicationState as State } from '../../store';
import { addParamToUrl } from '../../services/tools/handleUrlParams';
import { PreviewOverlay } from '../TemplatePreviewComponent/WebsiteMode/PreviewOverlay';
import { getStepsValidity } from '../FormCreator/tools/getStepsValidity';
import { StepPublishContent } from '../FormCreator/steps/StepPublishContent';
import { FormPublishUnavailable } from './components/FormPublishUnavailable';
import { Step } from '../FormCreator/types';

declare const window: any;
declare const ee: any;

const mapState = (state: State, ownProps: FormListProps): FormListProps => {
    const getApiPath = (): string => {
        if (!state.config.isStandalone && window.ee) {
            return ee.api.path;
        }

        return APIPATH;
    };

    return {
        ...ownProps,
        config: state.config,
        account: state.account,
        browser: state.browser,
        templates: state.formList,
        APIPATH: getApiPath(),
        isMarketing: true,
        isFormList: true,
    };
};

interface DispatchProps {
    ShowDialog: (params: ShowDialogParams) => void;
    HideDialog: () => void;
    UpdateDialog: (params: ShowDialogParams) => void;
    FormDeleteStart: (name: string) => void;

    ArrayOfFormsDeleteStart: (formNames: Array<string>, params: StartLoadingFormsParams) => void;
    Push: (url) => void;
    Replace(url: string | Record<string, string>): void;
    LoadingFormsStart(params: StartLoadingFormsParams): void;
}
const dispatchProps = {
    ShowDialog,
    HideDialog,
    UpdateDialog,
    FormDeleteStart,
    ArrayOfFormsDeleteStart,
    Push,
    Replace,
    LoadingFormsStart,
};

type ExtendedProps = FormListProps & DispatchProps;

interface PreviewContent {
    name?: string;
    content?: string;
    isOpenBlocked?: boolean;
}

const FormList = (props: ExtendedProps): JSX.Element => {
    const { t } = props;
    const dispatch = useDispatch();
    const [previewContent, setPreviewContent] = useState<PreviewContent>(null);
    const paths = { ...FormsPaths };
    const [checkedIfHaveClassicForms, setCheckedIfHaveClassicForms] = useState(false);

    useEffect(() => {
        dispatch(CheckIfHaveClassicFormsStart());
    }, []);

    useEffect(() => {
        if (checkedIfHaveClassicForms) {
            return;
        }
        setCheckedIfHaveClassicForms(true);
    }, [props.templates.haveClassicForms]);

    const setUrlParams = (param: Record<string, any>): void => {
        const { Replace } = props ?? {};
        const urlParams = addParamToUrl(param);
        Replace({ search: urlParams });
    };
    const onPreviewClick = (form: Form, isOpenBlocked: boolean): void => {
        setPreviewContent({ content: form.Html, isOpenBlocked, name: form.Name });
    };
    const onPreviewClose = (): boolean => {
        setPreviewContent(null);
        return true;
    };
    const formsRedirectFromOldDesigner = (name: string): void => {
        const { Push, config } = props;
        const copyForm = config?.isStandalone
            ? `${FormsPaths.LegacyCopyForm}/${safelyEncodeBase64Url(name)}`
            : `${FormsPaths.CopyForm}/${safelyEncodeBase64Url(name)}`;
        Push(copyForm);
    };

    const onCopyClick = (form: Form) => {
        if (form.FormType !== FormTypeEnum.FormCreator) {
            formsRedirectFromOldDesigner(form.Name);
            return;
        }

        const params: ShowDialogParams = {
            title: t('copyform'),
            confirmLabel: t('copy'),
            onClickConfirm: (): void => {
                dispatch(CopyFormStart(form.Name));
            },
            cancelLabel: t('cancel'),
            body: t('copyformconfirmation_msg', { name: form.Name }),
        };

        props.ShowDialog(params);
    };

    const onPublishClick = (form: Form) => {
        const formValidity = getStepsValidity(form);
        const isFormValid = formValidity[Step.Publish];
        const body = isFormValid ? <StepPublishContent formObject={form} /> : <FormPublishUnavailable />;

        const params: ShowDialogParams = {
            title: t('publish'),
            cancelLabel: t('close'),
            body,
            width: '870px',
        };

        props.ShowDialog(params);
    };

    return (
        <>
            {checkedIfHaveClassicForms && (
                <FormListModule
                    paths={paths}
                    retriveUrlParamsToAdd={setUrlParams}
                    {...props}
                    onPreviewClick={onPreviewClick}
                    onCopyClick={onCopyClick}
                    onPublishClick={onPublishClick}
                    isClassicDesignerHidden={!props.templates.haveClassicForms}
                />
            )}
            {previewContent && (
                <PreviewOverlay
                    fullWidthContent
                    name={previewContent.name}
                    previewContent={previewContent.content}
                    onClose={onPreviewClose}
                    isMobileAlertVisible={previewContent.isOpenBlocked}
                />
            )}
        </>
    );
};

export default compose(withTranslation(), connect<Props, DispatchProps>(mapState, dispatchProps))(FormList);
